import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../../utils/constants';
import { getAuthToken } from '../../../utils/auth';
import WorkflowDiffModal from '../../../Components/WorkflowFields/WorkflowDiffModal';
import WorkflowVersionHistory from '../../../Components/WorkflowFields/WorkflowVersionHistory';
import IconSelector from '../../../Components/IconSelector';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
// UUID generation function that works in all browsers
function generateUUID() {
  // Use crypto.randomUUID() if available (modern browsers)
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  
  // Fallback implementation for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Dependency types with descriptions
const DEPENDENCY_TYPES = [
  { 
    value: 'FINISH_TO_START', 
    label: 'Finish to Start',
    description: 'The dependent step can only start after the predecessor step has finished completely. Most common dependency type.'
  },
  { 
    value: 'START_TO_START', 
    label: 'Start to Start',
    description: 'The dependent step can start as soon as the predecessor step starts. Both can run in parallel.'
  },
  { 
    value: 'FINISH_TO_FINISH', 
    label: 'Finish to Finish',
    description: 'The dependent step cannot finish until the predecessor step finishes. Both can run in parallel.'
  },
  { 
    value: 'START_TO_FINISH', 
    label: 'Start to Finish',
    description: 'The dependent step cannot finish until the predecessor step starts. Least common dependency type.'
  }
];

// Workflow types
const WORKFLOW_TYPES = [
  { id: 'new-request', name: 'New Request' },
  { id: 'renew', name: 'Renew' },
  { id: 'lost-id', name: 'Lost ID' },
  { id: 'ohc', name: 'OHC (Medical)' }
];

const BasicWorkflowCreator = () => {
  const navigate = useNavigate();
  
  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Basic Information', 'Define Steps', 'Configure Services', 'Preview & Publish'];
  
  // Services state
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  // Diff modal state (Phase 2)
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffResult, setDiffResult] = useState(null);
  const [changeNotes, setChangeNotes] = useState('');
  // Preview tabs and definitions for side-by-side view
  const [previewTab, setPreviewTab] = useState(0); // 0 = Preview, 1 = Changes
  const [baseDefinition, setBaseDefinition] = useState(null);
  const [proposedDefinition, setProposedDefinition] = useState(null);
  // This state will store the server-generated JSON when it's available (declare early to avoid TDZ)
  const [serverGeneratedJson, setServerGeneratedJson] = useState(null);
  // Version history modal
  const [historyOpen, setHistoryOpen] = useState(false);
  
  // Workflow state
  const [workflow, setWorkflow] = useState({
    name: '',
    description: '',
    workflowType: '',
    variant: 'INSIDE',
    targetType: 'EMPLOYEE',
    version: 1,
    isActive: false,
    customerUid: generateUUID(),
    steps: [],
    organizationDocumentTypeIds: [],
    icon: null,   // Icon name (UI only for now - backend deferred)
    color: null   // Color hex (UI only for now - backend deferred)
  });

  // Organization document types (for ORGANIZATION workflows)
  const [orgDocTypes, setOrgDocTypes] = useState([]);
  
  // Current step being edited
  const [currentStep, setCurrentStep] = useState({
    id: '',
    stepNumber: 1,
    name: '',
    serviceId: '',
    serviceName: '',
    executionMode: 'SEQUENTIAL',
    dependencies: [],
    isRequired: true,
    fields: [],         // Regular service fields
    staticFields: []    // Static customer info fields
  });
  
  // Dependency dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [currentStepId, setCurrentStepId] = useState('');
  const [dependencyForm, setDependencyForm] = useState({
    stepId: '',
    type: 'FINISH_TO_START'
  });
  
  // State to track if we're editing an existing workflow
  const [isEditing, setIsEditing] = useState(false);
  const [originalWorkflowId, setOriginalWorkflowId] = useState(null);

  // Fetch services and check for workflow to edit on component mount
  useEffect(() => {
    const fetchServicesAndWorkflow = async () => {
      setLoading(true);
      try {
        // Get authentication token
        const token = getAuthToken();
        if (!token) {
          console.error('No authentication token found');
          throw new Error('Authentication token is required to fetch services');
        }

        console.log('Fetching services from API for targetType:', workflow.targetType);
        const response = await axios.get(`${API_BASE_URL}/api/services`, {
          params: { targetType: workflow.targetType || 'EMPLOYEE' },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.data) {
          console.error('No service data returned from API');
          throw new Error('No service data available');
        }

        // Validate service data
        if (!Array.isArray(response.data)) {
          console.error('Invalid service data format:', response.data);
          throw new Error('Service data is not in the expected format');
        }

        // Transform and validate each service
        const formattedServices = response.data.map(service => {
          if (!service.id || !service.name) {
            console.error('Invalid service data:', service);
            throw new Error(`Service is missing required fields: ${JSON.stringify(service)}`);
          }
          return {
            id: service.id,
            name: service.name,
            description: service.description,
            groupName: service.groupName,
            pricing: {
              governmentCharge: service.govtCharge,
              serviceCharge: service.serviceCharge,
              taxRate: service.taxOnServiceCharge
            }
          };
        });

        console.log('Successfully fetched and validated services:', formattedServices);
        setServices(formattedServices);
        
        // Check if we're editing an existing workflow
        const workflowToEdit = localStorage.getItem('workflow_to_edit');
        if (workflowToEdit) {
          const parsedWorkflow = JSON.parse(workflowToEdit);
          console.log('Loading workflow for editing:', parsedWorkflow);
          
          // Convert the workflow format if needed
          let workflowData = parsedWorkflow;
          
          // If it's in the new format with definition field
          if (parsedWorkflow.definition) {
            workflowData = {
              id: parsedWorkflow.id,
              name: parsedWorkflow.name,
              description: parsedWorkflow.description,
              workflowType: parsedWorkflow.definition.type,
              variant: parsedWorkflow.definition.variant,
              targetType: parsedWorkflow.targetType || 'EMPLOYEE',
              version: parsedWorkflow.version || 1,
              isActive: parsedWorkflow.isActive || false,
              organizationDocumentTypeIds: [],
              steps: parsedWorkflow.definition.steps.map(step => ({
                id: step.id,
                name: step.name,
                serviceId: step.serviceId,
                serviceName: step.serviceName,
                executionMode: step.executionMode,
                isRequired: step.required,
                dependencies: step.dependencies
              }))
            };
          }

          // Ensure targetType and organizationDocumentTypeIds are set from the parsed workflow
          workflowData.targetType = parsedWorkflow.targetType || workflowData.targetType || 'EMPLOYEE';
          workflowData.organizationDocumentTypeIds = (parsedWorkflow.organizationDocumentTypes || []).map(dt => dt.id);

          setWorkflow(prev => ({ ...prev, ...workflowData }));
          setOriginalWorkflowId(workflowData.id);
          setIsEditing(true);
          
          // Clear the localStorage item to prevent reloading on refresh
          localStorage.removeItem('workflow_to_edit');
        }
      } catch (error) {
        console.error('Error in initialization:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServicesAndWorkflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow.targetType]);

  // Fetch organization document types when target type is ORGANIZATION
  useEffect(() => {
    if (workflow.targetType === 'ORGANIZATION') {
      const fetchOrgDocTypes = async () => {
        try {
          const token = getAuthToken();
          const response = await axios.get(`${API_BASE_URL}/api/organization-document-types`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (Array.isArray(response.data)) {
            setOrgDocTypes(response.data);
          }
        } catch (err) {
          console.error('Error fetching organization document types:', err);
        }
      };
      fetchOrgDocTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow.targetType]);

  // Handle workflow form changes
  const handleWorkflowChange = (e) => {
    const { name, value } = e.target;
    setWorkflow({ ...workflow, [name]: value });
  };
  
  // Handle target type change — clear steps since services will differ
  const handleTargetTypeChange = (e) => {
    setWorkflow({ ...workflow, targetType: e.target.value, steps: [], organizationDocumentTypeIds: [] });
  };

  // Handle variant selection
  const handleVariantChange = (e) => {
    setWorkflow({ ...workflow, variant: e.target.value });
  };
  
  // Handle step form changes
  const handleStepChange = (e) => {
    const { name, value } = e.target;
    
    // If changing the service, fetch fields and suggest a step name
    if (name === 'serviceId') {
      const selectedService = services.find(service => service.id === value);
      if (selectedService) {
        // Use only the service name without any additional information
        const serviceName = selectedService.name;
        // Remove any group or category information that might be in the name
        const cleanServiceName = serviceName.split(' - ').pop().trim();
        
        // Get service details to extract fields
        const token = localStorage.getItem('jwt_token') || localStorage.getItem('token');
        
        // First get the service details to get the serviceGroupId
        axios.get(`${API_BASE_URL}/api/services/details?serviceId=${value}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : `Basic ${btoa('admin:admin')}`,
            'Content-Type': 'application/json'
          }
        })
        .then(response => {
          const serviceDetails = response.data;
          if (!serviceDetails || !serviceDetails.serviceGroupId) {
            throw new Error('No service group ID found in service details');
          }

          // Now get the service group details to get the fields
          return axios.get(`${API_BASE_URL}/v1/serviceGroup/details?groupId=${serviceDetails.serviceGroupId}`, {
            headers: {
              'Authorization': token ? `Bearer ${token}` : `Basic ${btoa('admin:admin')}`,
              'Content-Type': 'application/json'
            }
          });
        })
        .catch(error => {
          console.error('Error fetching service details:', error);
          throw error;
        })
        .then(response => {
          const groupDetails = response.data;
          if (!groupDetails || !groupDetails.fields) {
            throw new Error('No fields found in service group details');
          }

          // Format dynamic fields from group details
          const formattedDynamicFields = groupDetails.fields.map(field => {
            if (!field.id || !field.code) {
              console.error('Invalid field data:', field);
              throw new Error(`Missing required field properties: ${JSON.stringify(field)}`);
            }
            return {
              code: field.code,  // Use the code field as it's more user-friendly
              type: field.type,
              mandatory: field.mandatory,
              category: field.category || 'general',
              subCategory: field.subCategory,
              isStatic: false,
              title: field.title || field.code,
              validation: field.validation,  // Include validation rules
              options: field.options  // Include options for SELECT fields
            };
          });

          // Format static fields from staticFields array
          const formattedStaticFields = (groupDetails.staticFields || []).map(fieldCode => {
            return {
              code: fieldCode,
              type: 'TEXT',  // Default type for static fields
              mandatory: true,  // Static fields are typically mandatory
              category: 'Static',  // Special category for static fields
              isStatic: true,
              title: fieldCode  // Use the field code as the title
            };
          });
          
          // Keep fields and staticFields separate to prevent duplication
          // Don't combine them into a single array to avoid static field duplication regression
          
          console.log('Service fields processing:');
          console.log('- Dynamic fields:', formattedDynamicFields.length);
          console.log('- Static fields:', formattedStaticFields.length);
          
          setCurrentStep({
            ...currentStep,
            id: `step-${Date.now()}`,
            serviceId: value,
            serviceName: cleanServiceName,
            name: currentStep.name || cleanServiceName,
            fields: formattedDynamicFields,     // Only dynamic fields
            staticFields: formattedStaticFields // Only static fields
          });
        });
      } else {
        setCurrentStep({ ...currentStep, serviceId: value, serviceName: '' });
      }
    } else {
      setCurrentStep({ ...currentStep, [name]: value });
    }
  };
  
  // Add a step to the workflow
  const addStep = () => {
    const stepId = `step-${Date.now()}`;
    const selectedService = services.find(s => s.id === currentStep.serviceId);
    
    // Clean the service name to remove any group information
    let serviceName = '';
    if (selectedService) {
      serviceName = selectedService.name;
      // Remove any group or category information that might be in the name
      serviceName = serviceName.split(' - ').pop().trim();
    }
    
    // Create step with separate fields and staticFields arrays
    const newStep = {
      id: stepId,
      stepNumber: workflow.steps.length + 1,
      name: currentStep.name,
      serviceId: currentStep.serviceId,
      serviceName: serviceName,
      executionMode: currentStep.executionMode,
      dependencies: [],
      fields: currentStep.fields || [],           // Regular service fields
      staticFields: currentStep.staticFields || [], // Static customer info fields
      isRequired: currentStep.isRequired
    };
    
    console.log('Adding new workflow step with:');
    console.log(`- ${newStep.fields.length} regular fields`); 
    console.log(`- ${newStep.staticFields.length} static fields`);
    
    setWorkflow({
      ...workflow,
      steps: [...workflow.steps, newStep]
    });
    
    // Reset current step form - include separate fields and staticFields arrays
    setCurrentStep({
      id: '',
      stepNumber: workflow.steps.length + 2,
      name: '',
      serviceId: '',
      serviceName: '',
      executionMode: 'SEQUENTIAL',
      dependencies: [],
      fields: [],           // Reset regular fields
      staticFields: [],     // Reset static fields
      isRequired: true
    });
  };
  
  // Remove a step from the workflow
  const removeStep = (stepId) => {
    // Remove the step
    const updatedSteps = workflow.steps.filter(step => step.id !== stepId);
    
    // Update step numbers
    const renumberedSteps = updatedSteps.map((step, index) => ({
      ...step,
      stepNumber: index + 1
    }));
    
    // Remove any dependencies that reference the removed step
    const cleanedSteps = renumberedSteps.map(step => ({
      ...step,
      dependencies: step.dependencies.filter(dep => 
        updatedSteps.some(s => s.id === dep.stepId)
      )
    }));
    
    setWorkflow({
      ...workflow,
      steps: cleanedSteps
    });
    
    // Update current step number
    setCurrentStep({
      ...currentStep,
      stepNumber: cleanedSteps.length + 1
    });
  };
  
  // Open dependency dialog for a specific step
  const openDependencyDialog = (stepId) => {
    setCurrentStepId(stepId);
    setOpenDialog(true);
  };
  
  // Close dependency dialog
  const closeDependencyDialog = () => {
    setOpenDialog(false);
    setDependencyForm({
      stepId: '',
      type: 'FINISH_TO_START'
    });
  };
  
  // Handle dependency form changes
  const handleDependencyChange = (e) => {
    const { name, value } = e.target;
    setDependencyForm({ ...dependencyForm, [name]: value });
  };
  
  // Add dependency to a step
  const addDependency = () => {
    if (!dependencyForm.stepId || !currentStepId) return;
    
    // Find the step to update
    const updatedSteps = workflow.steps.map(step => {
      if (step.id === currentStepId) {
        // Check if dependency already exists
        const dependencyExists = step.dependencies.some(
          dep => dep.stepId === dependencyForm.stepId && dep.type === dependencyForm.type
        );
        
        if (dependencyExists) return step;
        
        // Add new dependency
        return {
          ...step,
          dependencies: [
            ...step.dependencies,
            {
              stepId: dependencyForm.stepId,
              type: dependencyForm.type
            }
          ]
        };
      }
      return step;
    });
    
    setWorkflow({
      ...workflow,
      steps: updatedSteps
    });
    
    closeDependencyDialog();
  };
  
  // Remove dependency from a step
  const removeDependency = (stepId, dependencyIndex) => {
    const updatedSteps = workflow.steps.map(step => {
      if (step.id === stepId) {
        const updatedDependencies = [...step.dependencies];
        updatedDependencies.splice(dependencyIndex, 1);
        
        return {
          ...step,
          dependencies: updatedDependencies
        };
      }
      return step;
    });
    
    setWorkflow({
      ...workflow,
      steps: updatedSteps
    });
  };
  
  // Update service for a step
  const handleServiceSelection = (stepId, e) => {
    const serviceId = e.target.value;
    const selectedService = services.find(service => service.id === serviceId);
    
    const updatedSteps = workflow.steps.map(step => {
      if (step.id === stepId) {
        return {
          ...step,
          serviceId,
          serviceName: selectedService ? selectedService.name : ''
        };
      }
      return step;
    });
    
    setWorkflow({
      ...workflow,
      steps: updatedSteps
    });
  };
  
  // Effect to fetch server-generated JSON when reaching the preview step
  useEffect(() => {
    // If we're on the preview step and we have workflow steps defined
    if (activeStep === 3 && workflow.steps.length > 0) {
      console.log('📈 Preview step activated, fetching workflow JSON from server');
      fetchServerGeneratedJson();
    }
  }, [activeStep]);

  // Populate side-by-side view when switching to Changes tab
  useEffect(() => {
    const populateChangesView = async () => {
      if (previewTab !== 1) return;
      // Always compute the latest proposed definition
      const proposed = buildProposedDefinition();
      setProposedDefinition(proposed || {});

      // If editing and base is missing, try to fetch base from server
      if (isEditing && originalWorkflowId && !baseDefinition) {
        try {
          const token = getAuthToken();
          if (!token) return;
          const resp = await fetch(`${API_BASE_URL}/api/workflow/${originalWorkflowId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (resp.ok) {
            const data = await resp.json();
            const def = data?.definition;
            const base = typeof def === 'string' ? JSON.parse(def) : def;
            setBaseDefinition(base || proposed || {});
          } else {
            setBaseDefinition(proposed || {});
          }
        } catch (e) {
          console.warn('Failed to populate baseDefinition on tab switch', e);
          setBaseDefinition(proposed || {});
        }
      }
    };
    populateChangesView();
  }, [previewTab, serverGeneratedJson, workflow, isEditing, originalWorkflowId]);

  // Handle stepper navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  

  // Function to fetch JSON from the server API
  const fetchServerGeneratedJson = async () => {
    try {
      console.log('🌐 CALLING SERVER API FOR WORKFLOW JSON');
      
      // Get auth token
      const token = getAuthToken();
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      // Prepare the payload for the server - using the multi-service endpoint format
      // Include separate fields and staticFields arrays to prevent duplication
      const payload = {
        name: workflow.name || `workflow${Math.floor(Math.random() * 100)}`,
        description: workflow.description || 'Generated workflow',
        type: workflow.workflowType || 'new-request',
        variant: workflow.variant || 'INSIDE',
        customerUid: workflow.customerUid, // Use the actual workflow customer UID, no fallback
        serviceConfigs: workflow.steps.map(step => {
          console.log(`Processing step ${step.id} with ${step.fields?.length || 0} fields and ${step.staticFields?.length || 0} static fields`);
          
          return {
            serviceId: step.serviceId,
            stepId: step.id,
            executionMode: step.executionMode,
            required: step.isRequired,
            // Include separate arrays for fields and staticFields
            fields: step.fields || [],         // Regular service fields
            staticFields: step.staticFields || [] // Static customer info fields
          };
        })
      };
      
      console.log('📊 Sending payload to server:', JSON.stringify(payload, null, 2));
      
      // Call the authenticated API endpoint - using POST with the multi-service endpoint
      const url = `${API_BASE_URL}/api/workflow-templates/generate-multi-service`;
      console.log('🔗 Calling URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',  // Using POST for the multi-service endpoint
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)  // Send the payload in the request body
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const jsonData = await response.json();
      console.log('✅ Received server-generated JSON:', jsonData);
      
      // Store the server-generated JSON
      setServerGeneratedJson(jsonData);
      
      // Force a re-render of the JSON preview
      // This is a hack to make React update the view
      setWorkflow({...workflow});
      
    } catch (error) {
      console.error('❌ Error fetching workflow JSON from server:', error);
    }
  };
  
  // Build proposed workflow definition for diff/save from current state (Phase 2)
  const buildProposedDefinition = () => {
    // Prefer serverGeneratedJson if available, else construct a minimal definition from workflow state
    if (serverGeneratedJson && serverGeneratedJson.definition) {
      console.log('Using serverGeneratedJson.definition for proposedDefinition');
      return serverGeneratedJson.definition;
    }
    // Attempt to use current preview JSON if available
    const currentPreview = generateWorkflowJson();
    const previewDef = currentPreview && currentPreview.definition ? currentPreview.definition : null;
    if (previewDef) {
      console.log('Using generateWorkflowJson().definition for proposedDefinition');
      return previewDef;
    }
    // Construct minimal from state as last resort
    console.warn('Falling back to minimal proposedDefinition from state');
    return {
      type: workflow.workflowType || 'new-request',
      variant: workflow.variant || 'INSIDE',
      customerUid: workflow.customerUid,
      steps: (workflow.steps || []).map(step => ({
        id: step.id,
        name: step.name,
        serviceId: step.serviceId,
        serviceName: step.serviceName,
        executionMode: step.executionMode,
        required: step.isRequired,
        fields: step.fields || [],
        staticFields: step.staticFields || [],
        dependencies: (step.dependencies || []).map(d => ({ stepId: d.stepId, type: d.type }))
      }))
    };
  };

  // Preview Changes (diff) when editing (Phase 2)
  const previewChanges = async () => {
    try {
      if (!isEditing || !originalWorkflowId) {
        alert('Preview Changes is only available in edit mode.');
        return;
      }
      const token = getAuthToken();
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      console.log('Preview Changes clicked. isEditing:', isEditing, 'workflowId:', originalWorkflowId);
      const proposed = buildProposedDefinition();
      console.log('Proposed definition built:', proposed ? 'yes' : 'no', proposed && proposed.steps ? `steps=${proposed.steps.length}` : '');
      setProposedDefinition(proposed);
      // Fetch base (current) definition
      let baseDef = null;
      try {
        const baseResp = await fetch(`${API_BASE_URL}/api/workflow/${originalWorkflowId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (baseResp.ok) {
          const baseJson = await baseResp.json();
          const def = baseJson?.definition;
          baseDef = typeof def === 'string' ? JSON.parse(def) : def;
          setBaseDefinition(baseDef);
        }
      } catch (e) {
        console.warn('Could not fetch base definition for side-by-side view', e);
      }
      if (!baseDef) {
        console.warn('Base definition unavailable from server; falling back to proposed for side-by-side display');
        setBaseDefinition(proposed || {});
      }
      const url = `${API_BASE_URL}/api/workflow/${originalWorkflowId}/diff`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ proposedDefinition: proposed })
      });
      if (!resp.ok) {
        throw new Error(`Diff API error: ${resp.status}`);
      }
      const data = await resp.json();
      setDiffResult(data);
      setPreviewTab(1); // switch to Changes tab
    } catch (e) {
      console.error('Error previewing changes:', e);
      alert('Error previewing changes. Please check console and try again.');
    }
  };

  // Confirm save-as-new-version (Phase 2)
  const confirmSaveNewVersion = async () => {
    try {
      if (!isEditing || !originalWorkflowId) {
        alert('Save as new version is only available in edit mode.');
        return;
      }
      const token = getAuthToken();
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      const definition = buildProposedDefinition();
      const url = `${API_BASE_URL}/api/workflow/${originalWorkflowId}/versions`;
      const resp = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ definition, notes: changeNotes })
      });
      if (!resp.ok) {
        throw new Error(`Create version API error: ${resp.status}`);
      }
      const resJson = await resp.json();
      console.log('New version created:', resJson);
      setDiffOpen(false);
      alert('New workflow version created and set as active.');
      navigate('/admin/workflow/list');
    } catch (e) {
      console.error('Error saving new version:', e);
      alert('Error saving new version. Please check console and try again.');
    }
  };
  
  // Generate workflow JSON - now fetches from server API with no fallback
  const generateWorkflowJson = () => {
    console.log('📋 PREPARING WORKFLOW DATA FOR SERVER API CALL');
    
    // Log workflow steps
    if (workflow.steps && workflow.steps.length > 0) {
      console.log('📃 WORKFLOW STEPS (' + workflow.steps.length + ' total):');
      workflow.steps.forEach((step, index) => {
        console.log(`  STEP ${index+1} (ID: ${step.id}):`);
        console.log(`    Name: ${step.name}`); 
        console.log(`    Service: ${step.serviceName} (${step.serviceId})`);
        console.log(`    Required: ${step.isRequired}`);
        console.log(`    Execution Mode: ${step.executionMode}`);
      });
    } else {
      console.log('⚠️ NO WORKFLOW STEPS DEFINED');
    }

    // If we have server-generated JSON, return it
    if (serverGeneratedJson) {
      console.log('🔍 Using server-generated JSON from API');
      return serverGeneratedJson;
    }

    // Make an API call to get the workflow JSON from the server if we don't have it yet
    fetchServerGeneratedJson();
    
    // Return empty structure with a loading message to indicate we're waiting for server data
    return {
      loading: true,
      message: 'Fetching workflow data from server...',
      timestamp: new Date().toISOString()
    };
    
    // No fallback to client-side generation as requested
  };
  
  // For preview only - not used for actual JSON generation
  const getSampleStructure = () => {
    // Get the current date and time for versioning
    const now = new Date().toISOString();
    
    // Get user info from auth
    const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
    const username = authInfo.username || authInfo.sub || 'unknown';
    
    return {
      name: workflow.name,
      description: workflow.description,
      // Include version information
      version: isEditing ? (workflow.version || 1) + 1 : 1,  // Increment version if editing
      isActive: workflow.isActive !== undefined ? workflow.isActive : false,  // Default to inactive
      createdBy: workflow.createdBy || username,
      createdAt: workflow.createdAt || now,
      modifiedBy: username,
      modifiedAt: now,
      definition: {
        type: workflow.workflowType,
        variant: workflow.variant,
        customerUid: workflow.customerUid,
        steps: workflow.steps.map(step => ({
          id: step.id,
          name: step.name,
          serviceId: step.serviceId,
          serviceName: step.serviceName,
          executionMode: step.executionMode,
          required: step.isRequired,
          fields: step.fields || [],         // Regular service fields
          staticFields: step.staticFields || [], // Static customer info fields
          dependencies: step.dependencies.map(dep => ({
            stepId: dep.stepId,
            type: dep.type
          }))
        }))
      }
    };
  };

  // Save workflow as draft
  const saveWorkflow = async () => {
    try {
      const workflowData = {
        ...generateWorkflowJson(),
        definition: {
          ...generateWorkflowJson().definition,
          isDraft: true
        }
      };
      console.log('Saving workflow as draft:', workflowData);
      
      // Save to local storage
      const savedWorkflows = JSON.parse(localStorage.getItem('thaklis_workflows_drafts') || '[]');
      savedWorkflows.push(workflowData);
      localStorage.setItem('thaklis_workflows_drafts', JSON.stringify(savedWorkflows));
      
      alert('Workflow saved as draft!');
    } catch (error) {
      console.error('Error saving workflow draft:', error);
      alert('Error saving workflow draft. Please try again.');
    }
  };

  // Save version history of a workflow
  const saveVersionHistory = (workflowId, workflowData) => {
    try {
      // Get existing version history
      const versionHistory = JSON.parse(localStorage.getItem('thaklis_workflow_versions') || '{}');
      
      // Initialize array for this workflow if it doesn't exist
      if (!versionHistory[workflowId]) {
        versionHistory[workflowId] = [];
      }
      
      // Add current version to history (deep clone to avoid reference issues)
      const versionToSave = JSON.parse(JSON.stringify(workflowData));
      versionHistory[workflowId].push(versionToSave);
      
      // Keep only the last 10 versions to avoid excessive storage
      if (versionHistory[workflowId].length > 10) {
        versionHistory[workflowId] = versionHistory[workflowId].slice(-10);
      }
      
      // Save back to localStorage
      localStorage.setItem('thaklis_workflow_versions', JSON.stringify(versionHistory));
      console.log(`Saved version ${workflowData.version} to history for workflow ${workflowId}`);
    } catch (error) {
      console.error('Error saving version history:', error);
    }
  };

  // Publish workflow using the backend API
  const publishWorkflow = async () => {
    try {
      // Generate the workflow data in the format expected by the backend
      const workflowData = generateWorkflowJson();
      
      // If we're editing an existing workflow, add the original ID
      if (isEditing && originalWorkflowId) {
        workflowData.id = originalWorkflowId;
      }
      
      console.log(isEditing ? 'Updating workflow:' : 'Publishing new workflow:', workflowData);
      
      // Clean up workflow data to ensure static fields don't appear in both arrays
      const cleanedDefinition = { ...workflowData.definition };
      
      // Process each step to filter fields
      if (cleanedDefinition.steps && Array.isArray(cleanedDefinition.steps)) {
        cleanedDefinition.steps = cleanedDefinition.steps.map(step => {
          const staticFieldLabels = new Set();
          
          // Collect all static field names first
          if (step.staticFields && Array.isArray(step.staticFields)) {
            step.staticFields.forEach(staticField => {
              if (staticField.code) {
                staticFieldLabels.add(staticField.code);
              }
            });
          }
          
          // Filter regular fields to remove any that are in static fields
          if (step.fields && Array.isArray(step.fields)) {
            step.fields = step.fields.filter(field => {
              // Keep the field only if it's not a static field
              return !staticFieldLabels.has(field.label);
            });
          }
          
          return step;
        });
      }
      
      console.log('Cleaned workflow definition:', cleanedDefinition);
      
      // Prepare the API data in the format expected by the backend
      const apiWorkflowData = {
        name: workflowData.name,
        description: workflowData.description || '',
        definition: cleanedDefinition,
        targetType: workflow.targetType || 'EMPLOYEE',
        organizationDocumentTypeIds: workflow.targetType === 'ORGANIZATION' ? (workflow.organizationDocumentTypeIds || []) : []
      };
      
      console.log('Sending workflow data to API:', apiWorkflowData);
      
      // Get authentication token
      const token = getAuthToken();
      console.log('Token exists:', !!token);
      
      let response;
      
      if (isEditing) {
        // Edit: create a new version and set active via backend versioning endpoint
        if (!token) {
          console.error('No authentication token found for edit');
          throw new Error('Authentication token is required to update workflow');
        }
        const versionPayload = {
          definition: cleanedDefinition,
          notes: (changeNotes && changeNotes.trim()) ? changeNotes.trim() : 'UI publish',
          organizationDocumentTypeIds: workflow.targetType === 'ORGANIZATION' ? (workflow.organizationDocumentTypeIds || []) : []
        };
        console.log('Creating new workflow version via PUT /api/workflow/{id}/versions');
        response = await axios.put(`${API_BASE_URL}/api/workflow/${originalWorkflowId}/versions`, versionPayload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('New workflow version created:', response.data);
        // Optionally keep local history
        saveVersionHistory(originalWorkflowId, workflowData);
      } else {
        // Create new workflow
        if (token) {
          // Try with JWT token first
          try {
            console.log('Creating workflow with JWT token');
            response = await axios.post(`${API_BASE_URL}/api/workflow`, apiWorkflowData, {  
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            console.log('Workflow created successfully with JWT token, response:', response.data);
          } catch (error) {
            console.warn('JWT token failed for creation, trying admin credentials:', error);
            // Fallback to admin credentials
            response = await axios.post(`${API_BASE_URL}/api/workflow`, apiWorkflowData, {
              headers: {
                'Authorization': `Basic ${btoa('admin:admin')}`,
                'Content-Type': 'application/json'
              }
            });
            console.log('Workflow created successfully with admin credentials, response:', response.data);
          }
        } else {
          // No token, use admin credentials directly
          console.log('Creating workflow with admin credentials');
          response = await axios.post(`${API_BASE_URL}/api/workflow`, apiWorkflowData, {
            headers: {
              'Authorization': `Basic ${btoa('admin:admin')}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('Workflow created successfully with admin credentials, response:', response.data);
        }
      }
      
      // Store the workflow in localStorage as a backup
      const savedWorkflows = JSON.parse(localStorage.getItem('thaklis_workflows') || '[]');
      
      // If we have a response with the created/updated workflow, use that data
      const savedWorkflow = response?.data || workflowData;
      
      if (isEditing) {
        // Update existing workflow in localStorage
        const index = savedWorkflows.findIndex(w => w.id === originalWorkflowId);
        if (index !== -1) {
          savedWorkflows[index] = savedWorkflow;
        } else {
          savedWorkflows.push(savedWorkflow);
        }
      } else {
        // Add new workflow
        savedWorkflows.push(savedWorkflow);
      }
      
      localStorage.setItem('thaklis_workflows', JSON.stringify(savedWorkflows));
      
      alert(isEditing ? 'Workflow updated successfully!' : 'Workflow created successfully!');
      navigate('/admin/workflow/list');
    } catch (error) {
      console.error('Error publishing workflow:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      alert(`Error ${isEditing ? 'updating' : 'creating'} workflow: ${error.message}. Please try again.`);
    }
  };
  
  // Validate current step based on active step
  const validateCurrentStep = () => {
    switch (activeStep) {
      case 0:
        return workflow.name && workflow.workflowType;
      case 1:
        return workflow.steps.length > 0;
      case 2:
        return workflow.steps.every(step => step.serviceId);
      default:
        return true;
    }
  };
  
  // Render step content based on active step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Basic Workflow Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Workflow Name"
                  name="name"
                  value={workflow.name}
                  onChange={handleWorkflowChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={4}
                  value={workflow.description}
                  onChange={handleWorkflowChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Workflow Type</InputLabel>
                  <Select
                    name="workflowType"
                    value={workflow.workflowType}
                    onChange={handleWorkflowChange}
                    label="Workflow Type"
                  >
                    {WORKFLOW_TYPES.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Workflow Variant</FormLabel>
                  <RadioGroup
                    row
                    name="variant"
                    value={workflow.variant}
                    onChange={handleVariantChange}
                  >
                    <FormControlLabel
                      value="INSIDE"
                      control={<Radio />}
                      label="Inside"
                    />
                    <FormControlLabel
                      value="OUTSIDE"
                      control={<Radio />}
                      label="Outside"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" disabled={isEditing}>
                  <FormLabel component="legend">Target Type {isEditing && '(cannot change after creation)'}</FormLabel>
                  <RadioGroup
                    row
                    name="targetType"
                    value={workflow.targetType}
                    onChange={isEditing ? undefined : handleTargetTypeChange}
                  >
                    <FormControlLabel
                      value="EMPLOYEE"
                      control={<Radio />}
                      label="Employee"
                    />
                    <FormControlLabel
                      value="ORGANIZATION"
                      control={<Radio />}
                      label="Organization"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {workflow.targetType === 'ORGANIZATION' && orgDocTypes.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Organization Document Types (Optional)</InputLabel>
                    <Select
                      multiple
                      name="organizationDocumentTypeIds"
                      value={workflow.organizationDocumentTypeIds || []}
                      onChange={(e) => setWorkflow({ ...workflow, organizationDocumentTypeIds: e.target.value })}
                      label="Organization Document Types (Optional)"
                      renderValue={(selected) =>
                        selected.map(id => orgDocTypes.find(dt => dt.id === id)?.name || id).join(', ')
                      }
                    >
                      {orgDocTypes.map((dt) => (
                        <MenuItem key={dt.id} value={dt.id}>
                          {dt.name} ({dt.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {/* Icon & Color Selection (UI only - backend deferred) */}
              <Grid item xs={12}>
                <IconSelector
                  selectedIcon={workflow.icon}
                  selectedColor={workflow.color}
                  onIconChange={(icon) => setWorkflow(prev => ({ ...prev, icon }))}
                  onColorChange={(color) => setWorkflow(prev => ({ ...prev, color }))}
                  label="Icon & Color"
                  showPreview={true}
                  previewText={workflow.name || 'Workflow Preview'}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Define Workflow Steps
            </Typography>
            
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Add New Step
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Step Name"
                    name="name"
                    value={currentStep.name}
                    onChange={handleStepChange}
                    placeholder={currentStep.serviceName ? `${currentStep.serviceName}` : "Enter step name"}
                    helperText="Step name will be suggested based on selected service"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Execution Mode</InputLabel>
                    <Select
                      name="executionMode"
                      value={currentStep.executionMode}
                      onChange={handleStepChange}
                      label="Execution Mode"
                    >
                      <MenuItem value="SEQUENTIAL">Sequential</MenuItem>
                      <MenuItem value="PARALLEL">Parallel</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Select Service</InputLabel>
                    <Select
                      name="serviceId"
                      value={currentStep.serviceId}
                      onChange={handleStepChange}
                      label="Select Service"
                    >
                      {/* Group services by category for better organization */}
                      {services.length > 0 ? (
                        services
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(service => (
                            <MenuItem 
                              key={service.id} 
                              value={service.id}
                            >
                              {service.name}
                              {service.description && service.description !== service.name + ' service' && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  - {service.description}
                                </Typography>
                              )}
                            </MenuItem>
                          ))
                      ) : (
                        <MenuItem disabled>No services available</MenuItem>
                      )}
                    </Select>
                    <FormHelperText>Select a service for this workflow step</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={currentStep.isRequired}
                        onChange={(e) => 
                          setCurrentStep({
                            ...currentStep,
                            isRequired: e.target.checked
                          })
                        }
                      />
                    }
                    label="Required Step"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={addStep}
                    disabled={!currentStep.name}
                  >
                    Add Step
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>
              Current Steps ({workflow.steps.length})
            </Typography>
            
            {workflow.steps.length > 0 ? (
              <Grid container spacing={2}>
                {workflow.steps.map((step) => (
                  <Grid item xs={12} key={step.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="h6">
                            {step.stepNumber}. {step.name}
                          </Typography>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => removeStep(step.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          Execution Mode: {step.executionMode}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Required: {step.isRequired ? 'Yes' : 'No'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body1" color="textSecondary">
                No steps added yet. Add steps to define your workflow.
              </Typography>
            )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Configure Services for Steps
            </Typography>
            
            {workflow.steps.length > 0 ? (
              <Grid container spacing={3}>
                {workflow.steps.map((step) => (
                  <Grid item xs={12} key={step.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">
                          {step.stepNumber}. {step.name}
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Select Service</InputLabel>
                            <Select
                              value={step.serviceId}
                              onChange={(e) => handleServiceSelection(step.id, e)}
                              label="Select Service"
                            >
                              {services.map((service) => (
                                <MenuItem key={service.id} value={service.id}>
                                  {service.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle2">Dependencies</Typography>
                              <Button 
                                size="small" 
                                startIcon={<AddIcon />}
                                onClick={() => openDependencyDialog(step.id)}
                                disabled={workflow.steps.length <= 1 || step.stepNumber === 1}
                              >
                                Add Dependency
                              </Button>
                            </Box>
                            
                            {step.dependencies.length > 0 ? (
                              <Box sx={{ mt: 1 }}>
                                {step.dependencies.map((dep, idx) => {
                                  const depStep = workflow.steps.find(s => s.id === dep.stepId);
                                  return (
                                    <Tooltip 
                                      key={idx}
                                      title={DEPENDENCY_TYPES.find(t => t.value === dep.type)?.description || ''}
                                      placement="top"
                                      arrow
                                    >
                                      <Chip
                                        label={depStep?.name || 'Unknown'}
                                        onDelete={() => removeDependency(step.id, idx)}
                                        sx={{ m: 0.5 }}
                                      />
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No dependencies
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body1" color="textSecondary">
                No steps have been defined yet. Go back to add steps first.
              </Typography>
            )}
            
            {/* Dependency Dialog */}
            <Dialog open={openDialog} onClose={closeDependencyDialog}>
              <DialogTitle>Add Dependency</DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Depends on Step</InputLabel>
                      <Select
                        name="stepId"
                        value={dependencyForm.stepId}
                        onChange={handleDependencyChange}
                        label="Depends on Step"
                      >
                        {/* Check if there are any previous steps available */}
                        {workflow.steps
                          .filter(step => {
                            // Get the current step
                            const currentStep = workflow.steps.find(s => s.id === currentStepId);
                            if (!currentStep) return false;
                            
                            // Only show steps that are before the current step
                            return step.id !== currentStepId && step.stepNumber < currentStep.stepNumber;
                          })
                          .map((step) => {
                            // Check if this step is already a dependency
                            const currentStepObj = workflow.steps.find(s => s.id === currentStepId);
                            const isAlreadyDependency = currentStepObj?.dependencies?.some(dep => dep.stepId === step.id);
                            
                            return (
                              <MenuItem 
                                key={step.id} 
                                value={step.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <Box>
                                  <Typography variant="body1">
                                    {step.stepNumber}. {step.name}
                                  </Typography>
                                </Box>
                                {isAlreadyDependency && (
                                  <Chip 
                                    size="small" 
                                    label="Already Added" 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </MenuItem>
                            );
                          })
                        }
                        {workflow.steps.filter(step => {
                          const currentStep = workflow.steps.find(s => s.id === currentStepId);
                          if (!currentStep) return false;
                          return step.id !== currentStepId && step.stepNumber < currentStep.stepNumber;
                        }).length === 0 && (
                          <MenuItem disabled>
                            No previous steps available for dependency
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Dependency Type</InputLabel>
                      <Select
                        name="type"
                        value={dependencyForm.type}
                        onChange={handleDependencyChange}
                        label="Dependency Type"
                      >
                        {DEPENDENCY_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 1, mt: 1, bgcolor: 'info.light', color: 'info.contrastText' }}>
                      <Typography variant="caption">
                        <strong>{DEPENDENCY_TYPES.find(t => t.value === dependencyForm.type)?.label}:</strong> {DEPENDENCY_TYPES.find(t => t.value === dependencyForm.type)?.description}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDependencyDialog}>Cancel</Button>
                <Button 
                  onClick={addDependency} 
                  variant="contained" 
                  disabled={!dependencyForm.stepId}
                >
                  Add
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Workflow Preview
            </Typography>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5">{workflow.name}</Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  Type: {WORKFLOW_TYPES.find(t => t.id === workflow.workflowType)?.name || 'Not specified'}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  Variant: {workflow.variant}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {workflow.description || 'No description provided.'}
                </Typography>
              </CardContent>
            </Card>
            
            <Typography variant="h6" gutterBottom>
              Steps ({workflow.steps.length})
            </Typography>
            
            {workflow.steps.length > 0 ? (
              <Grid container spacing={2}>
                {workflow.steps.map((step) => (
                  <Grid item xs={12} key={step.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">
                          {step.stepNumber}. {step.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Service: {step.serviceName || 'Not selected'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Execution Mode: {step.executionMode}
                        </Typography>
                        
                        {step.dependencies.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">
                              Dependencies:
                            </Typography>
                            {step.dependencies.map((dep, idx) => {
                              const depStep = workflow.steps.find(s => s.id === dep.stepId);
                              return (
                                <Tooltip 
                                  key={idx}
                                  title={DEPENDENCY_TYPES.find(t => t.value === dep.type)?.description || ''}
                                  placement="top"
                                  arrow
                                >
                                  <Chip
                                    size="small"
                                    label={`${depStep?.name || 'Unknown'} (${dep.type})`}
                                    sx={{ m: 0.5 }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body1" color="textSecondary">
                No steps defined for this workflow.
              </Typography>
            )}
            
            <Box sx={{ mt: 4 }}>
              <Tabs value={previewTab} onChange={(e, v) => setPreviewTab(v)}>
                <Tab label="Preview" />
                <Tab label="Changes" />
              </Tabs>
              {/* Preview Tab */}
              {previewTab === 0 && (
                <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  <pre>{JSON.stringify(generateWorkflowJson(), null, 2)}</pre>
                </Paper>
              )}
              {/* Changes Tab */}
              {previewTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  {/* Summary on top */}
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Change Summary
                      </Typography>
                      {!diffResult && (
                        <Typography variant="body2" color="textSecondary">Click "Preview Changes" to compute the diff.</Typography>
                      )}
                      {diffResult && (
                        <Typography variant="body2">
                          Adds: {diffResult?.stats?.adds || 0}, Removes: {diffResult?.stats?.removes || 0}, Replaces: {diffResult?.stats?.replaces || 0}
                        </Typography>
                      )}
                      {diffResult?.summary?.message && (
                        <Typography variant="body2" color="textSecondary">
                          {diffResult.summary.message}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                  {/* Side-by-side JSON */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Current (Old)
                      </Typography>
                      <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                        <pre>{JSON.stringify(baseDefinition ?? {}, null, 2)}</pre>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Proposed (New)
                      </Typography>
                      <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                        <pre>{JSON.stringify(proposedDefinition ?? {}, null, 2)}</pre>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleBack}
              >
                Back
              </Button>
              <Box>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => setHistoryOpen(true)}
                    sx={{ mr: 1 }}
                  >
                    Version History
                  </Button>
                )}
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={previewChanges}
                    sx={{ mr: 1 }}
                  >
                    Preview Changes
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={publishWorkflow}
                  disabled={workflow.steps.length === 0}
                >
                  Publish Workflow
                </Button>
              </Box>
            </Box>
            {/* Diff Modal */}
            {isEditing && (
              <WorkflowDiffModal
                open={diffOpen}
                onClose={() => setDiffOpen(false)}
                onConfirm={confirmSaveNewVersion}
                diff={diffResult}
                notes={changeNotes}
                setNotes={setChangeNotes}
              />
            )}
            {/* Version History Dialog */}
            {isEditing && originalWorkflowId && (
              <WorkflowVersionHistory
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                workflowId={originalWorkflowId}
              />
            )}
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {isEditing ? 'Edit Workflow' : 'Create New Workflow'}
        </Typography>
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
          
        {getStepContent(activeStep)}
          
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={activeStep === steps.length - 1 || !validateCurrentStep()}
            endIcon={<ArrowForwardIcon />}
          >
            Next
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BasicWorkflowCreator;
